import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import PhotoInput from "./PhotoInput";
import { useLanguage } from "../i18n/LanguageContext";

const empty = {
  unit: "", name: "", role: "Ocupante", email: "", altEmail: "",
  whatsapp: "", phone: "", photo: null, preferredLanguage: "es",
};

export default function OccupantDialog({ open, initial, onClose, onSave }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(initial || empty);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Reinicia el formulario cada vez que se abre con datos distintos.
  // (Antes esto usaba useState(fn, deps) por error — useState no acepta
  // un arreglo de dependencias, así que el "efecto" solo corría una vez
  // al montar y el formulario nunca se reiniciaba entre aperturas.)
  useEffect(() => {
    if (open) setForm(initial || empty);
  }, [initial, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.unit.trim() || !form.name.trim() || !form.email.trim()) {
      setError(t("occupantDialog.missingFields"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(form);
    } catch (err) {
      setError(err.response?.data?.error || t("occupantDialog.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {initial ? t("occupantDialog.titleEdit") : t("occupantDialog.titleAdd")}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <PhotoInput
            value={form.photo ? [form.photo] : []}
            onChange={(arr) => setForm((f) => ({ ...f, photo: arr[0] || null }))}
            max={1}
          />
        </Box>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField label={t("occupantDialog.unit")} value={form.unit} onChange={set("unit")} fullWidth />
            <TextField select label={t("occupantDialog.type")} value={form.role} onChange={set("role")} fullWidth>
              <MenuItem value="Propietario">{t("occupantDialog.typeOwner")}</MenuItem>
              <MenuItem value="Ocupante">{t("occupantDialog.typeOccupant")}</MenuItem>
            </TextField>
          </Stack>
          <TextField label={t("occupantDialog.fullName")} value={form.name} onChange={set("name")} fullWidth />
          <TextField label={t("occupantDialog.email")} type="email" value={form.email} onChange={set("email")} fullWidth />
          <TextField
            label={t("occupantDialog.altEmail")}
            type="email"
            value={form.altEmail}
            onChange={set("altEmail")}
            placeholder={t("occupantDialog.altEmailPlaceholder")}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField label={t("occupantDialog.whatsapp")} value={form.whatsapp} onChange={set("whatsapp")} fullWidth />
            <TextField label={t("occupantDialog.phone")} value={form.phone} onChange={set("phone")} fullWidth />
          </Stack>
          <TextField select label={t("occupantDialog.preferredLanguage")} value={form.preferredLanguage || "es"} onChange={set("preferredLanguage")} fullWidth>
            <MenuItem value="es">{t("occupantDialog.langEs")}</MenuItem>
            <MenuItem value="en">{t("occupantDialog.langEn")}</MenuItem>
          </TextField>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="contained" startIcon={<CheckIcon />} onClick={handleSubmit} disabled={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
